/**
 * Trunnion AI - Agent Node
 * Custom node displaying agent name, role, clearance badge, mode, and tool count.
 */

import { memo } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { Bot, Wrench, Shield, Eye, Zap } from 'lucide-react';
import type { AgentNodeData } from '@/types';
import { getClassificationClasses, getClassificationShortLabel } from '@/lib/classification';
import { useWorkflowDesignerStore } from '@/stores/workflowDesignerStore';

const modeIcons = {
  autonomous: Zap,
  supervised: Eye,
  manual: Shield,
};

const modeLabels = {
  autonomous: 'Autonomous',
  supervised: 'Supervised',
  manual: 'Manual',
};

const tierColors = {
  orchestrator: 'text-trunnion-gold-400',
  specialist: 'text-blue-400',
  utility: 'text-trunnion-slate-300',
};

export const AgentNode = memo(function AgentNode({
  id,
  data,
  selected,
}: NodeProps & { data: AgentNodeData }) {
  const setSelectedNodeId = useWorkflowDesignerStore((s) => s.setSelectedNodeId);
  const classClasses = getClassificationClasses(data.classification);
  const ModeIcon = modeIcons[data.mode as keyof typeof modeIcons];

  return (
    <div
      className={`min-w-65 rounded-xl border-2 bg-trunnion-slate-800 shadow-lg transition-all ${
        selected
          ? 'border-trunnion-gold-500 shadow-glow-gold'
          : `border-trunnion-slate-600 hover:border-trunnion-slate-500`
      }`}
      onClick={() => setSelectedNodeId(id)}
    >
      {/* Header */}
      <div className="flex items-center justify-between rounded-t-[10px] border-b border-trunnion-slate-700 bg-trunnion-slate-800/80 px-3 py-2">
        <div className="flex items-center gap-2">
          <Bot className={`h-4 w-4 ${tierColors[data.tier as keyof typeof tierColors]}`} />
          <span className="text-sm font-semibold text-white truncate max-w-40">
            {data.label || 'Agent'}
          </span>
        </div>
        <span
          className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${classClasses.bg} ${classClasses.text} ${classClasses.border}`}
        >
          <Shield className="h-2.5 w-2.5" />
          {getClassificationShortLabel(data.classification)}
        </span>
      </div>

      {/* Body */}
      <div className="px-3 py-2.5 space-y-1.5">
        {data.agentName && (
          <p className="text-xs text-trunnion-slate-300 truncate">
            Agent: <span className="text-white">{data.agentName}</span>
          </p>
        )}
        <div className="flex items-center gap-3 text-xs text-trunnion-slate-400">
          <span className="flex items-center gap-1">
            <ModeIcon className="h-3 w-3" />
            {modeLabels[data.mode as keyof typeof modeLabels]}
          </span>
          <span className="flex items-center gap-1">
            <Wrench className="h-3 w-3" />
            {data.toolCount} tools
          </span>
        </div>
        <div className="text-[10px] text-trunnion-slate-500 uppercase tracking-wider">
          {data.tier}
        </div>
      </div>

      {/* Handles */}
      <Handle
        type="target"
        position={Position.Top}
        className="!bg-trunnion-slate-800 !border-trunnion-gold-500"
      />
      <Handle
        type="source"
        position={Position.Bottom}
        className="!bg-trunnion-slate-800 !border-trunnion-gold-500"
      />
    </div>
  );
});
